import 'react-loading-skeleton/dist/skeleton.css';

import Visible from '@portalterreno/react-ui/Visible';
import Portal from '@portalterreno/react-ui-core/Portal';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { reset } from '../../redux/slices/contactForm';
import { RootState } from '../../redux/store';
import StyledCard from './FormValues/StyledCard';
import MobileContactForm, { MobileContactFormProps } from './MobileContactForm';
import RelatedPropertiesLeadModal from './RelatedPropertiesLeadModal';
import SentModal, { SentModalProps } from './SentModal';
import StyledHeaderTypography from './StyledHeaderTypography';

export type ContactFormProps = Pick<
  MobileContactFormProps,
  | 'campaignBarText'
  | 'campaignLink'
  | 'campaignLinkText'
  | 'onClickWhatsappButton'
  | 'onClickShowPhoneButton'
  | 'sellerWhatsapp'
  | 'propertyId'
  | 'formHeader'
> &
  Pick<SentModalProps, 'sellerPhone' | 'companyLogo' | 'sellerName'> &
  React.PropsWithChildren<{
    onSubmit: () => void;
  }>;

const ContactForm: React.FC<ContactFormProps> = ({
  onSubmit,
  companyLogo,
  propertyId,
  sellerName,
  campaignBarText,
  campaignLink,
  campaignLinkText,
  children,
  sellerPhone,
  sellerWhatsapp,
  onClickWhatsappButton,
  onClickShowPhoneButton,
  formHeader
}) => {
  const state = useSelector(
    (state: RootState) => ({
      status: state.contactForm.status,
      source: state.contactForm.source
    }),
    shallowEqual
  );
  const dispatch = useDispatch();

  const { t } = useTranslation('property');

  // reset form state on route change
  React.useEffect(() => {
    dispatch(reset());
  }, [dispatch, propertyId]);

  const handleOnSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      onSubmit();
    },
    [onSubmit]
  );

  const isSentModalOpen = React.useMemo(() => {
    return (
      state.status === 'error' ||
      (state.status === 'success' && state.source === 'contactForm')
    );
  }, [state.source, state.status]);

  return (
    <React.Fragment>
      <form
        onSubmit={handleOnSubmit}
        noValidate
      >
        <Visible mobile>
          <MobileContactForm
            campaignBarText={campaignBarText}
            campaignLink={campaignLink}
            campaignLinkText={campaignLinkText}
            onClickShowPhoneButton={onClickShowPhoneButton}
            onClickWhatsappButton={onClickWhatsappButton}
            onSubmit={onSubmit}
            propertyId={propertyId}
            sellerLogo={companyLogo}
            sellerName={sellerName}
            sellerPhone={sellerPhone}
            sellerWhatsapp={sellerWhatsapp}
            formHeader={formHeader}
          >
            {' '}
            {formHeader}
            {children}
          </MobileContactForm>
        </Visible>
        <Visible desktop>
          <StyledCard>
            <React.Fragment>
              <StyledHeaderTypography
                variant='subtitle1'
                weight='regular'
              >
                {t('headerTextContactForm')}
              </StyledHeaderTypography>
              {children}
            </React.Fragment>
          </StyledCard>
        </Visible>
      </form>
      <Portal>
        <SentModal
          companyLogo={companyLogo}
          isOpen={isSentModalOpen}
          sellerName={sellerName}
          sellerPhone={sellerPhone}
        />
      </Portal>
      <Portal>
        <RelatedPropertiesLeadModal />
      </Portal>
    </React.Fragment>
  );
};

export default ContactForm;
